import style from './AuditBody.module.css';

import styled from 'styled-components';
import { formatarData } from '../../../Funcoes/FormatarData';
import { FiEdit2, FiTrash2, FiEye, FiCalendar } from "react-icons/fi";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SortableTable from '../../Utils/SortableTable';
import GenericHeader from '../../GenericHeader';
import Filters from '../../Utils/Filters';
import FilterContainer from '../../Utils/FilterContainer';
import MultiselectCheck from '../../Utils/MultiselectCheck';
import useApi from '../../../hooks/useApi';
import { useToast } from '../../../contexts/ToastContext';

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const AuditBody = ({ dados, editItem, openView, dadosToFilter, handleFilter, onAuditDeleted }) => {

    const api = useApi();
    const { notifySuccess, notifyError } = useToast();

    const [obrasSelected, setObrasSelected] = useState([])
    const [contratosSelected, setContratosSelected] = useState([])
    const [statusSelected, setStatusSelected] = useState([])
    const [auditoresSelected, setAuditoresSelected] = useState([])

    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const obras = useMemo(
        () => [...new Set(dadosToFilter.map((obj) => obj.centro_custo))].sort(),
        [dadosToFilter]
    );
    const contratos = useMemo(
        () => [...new Set(dadosToFilter.map((obj) => obj.contrato))].sort(),
        [dadosToFilter]
    );
    const auditores = useMemo(
        () =>
            [...new Set(dadosToFilter.flatMap((obj) => obj.auditores ?? []))]
                .filter(Boolean)
                .sort(),
        [dadosToFilter]
    );

    const opcoesObrasMs = useMemo(
        () => obras.map((opt) => ({ value: opt, label: opt })),
        [obras]
    );
    const opcoesContratosMs = useMemo(
        () => contratos.map((opt) => ({ value: opt, label: opt })),
        [contratos]
    );
    const opcoesAuditoresMs = useMemo(
        () => auditores.map((opt) => ({ value: opt, label: opt })),
        [auditores]
    );
    const opcoesStatusMs = useMemo(
        () => [
            { value: 'ABERTO', label: 'ABERTO' },
            { value: 'CANCELADO', label: 'CANCELADO' },
            { value: 'EM ATRASO', label: 'EM ATRASO' },
            { value: 'FINALIZADO', label: 'FINALIZADO' },
            { value: 'PREVISTO', label: 'PREVISTO' },
            { value: 'REPROGRAMADO', label: 'REPROGRAMADO' },
        ],
        []
    );

    useEffect(() => {

        handleFilter(obrasSelected, contratosSelected, statusSelected, auditoresSelected, startDate, endDate)

    }, [obrasSelected, contratosSelected, statusSelected, auditoresSelected, startDate, endDate])

    const previstos = dados.filter(item => item.status === 'PREVISTO').length
    const abertos = dados.filter(item => item.status === 'ABERTO').length
    const finalizados = dados.filter(item => item.status === 'FINALIZADO').length
    const cancelados = dados.filter(item => item.status === 'CANCELADO').length
    const reprogramados = dados.filter(item => item.status === 'REPROGRAMADO').length

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const tabelaRef = useRef(null);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tabelaRef.current) {
            tabelaRef.current.scrollTop = 0;
        }
    }, [dados]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = dados.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [dados.length]);

    const handleSoftDelete = async (e, identifier) => {

        e.stopPropagation();

        if (!window.confirm('Remover esta auditoria da lista? (Os dados permanecem no sistema.)')) {
            return;
        }

        try {

            await api.post('/Painel/API/AuditInterna/SoftDeleteAudit/', { identifier });

            notifySuccess('Auditoria removida da lista.');
            if (typeof onAuditDeleted === 'function') {
                await onAuditDeleted();
            }

        } catch (error) {

            const msg = error.response?.data?.message ?? 'Falha ao remover a auditoria.';
            notifyError(msg);

        }

    };

    const columns = useMemo(
        () => [
            {
                id: 'id',
                header: 'ID',
                accessor: 'id',
                sortable: true,
                width: '70px',
                align: 'center',
            },
            {
                id: 'centro_custo',
                header: 'CENTRO CUSTO',
                accessor: 'centro_custo',
                sortable: true,
            },
            {
                id: 'contrato',
                header: 'CONTRATO',
                accessor: 'contrato',
                sortable: true,
            },
            {
                id: 'auditores',
                header: 'AUDITORES',
                accessor: 'auditores',
                sortable: true,
                render: (_value, row) =>
                    row.auditores?.length > 0 ? row.auditores.join(' | ') : '-',
                sortValue: (row) =>
                    row.auditores?.length > 0 ? row.auditores.join(' | ') : '',
            },
            {
                id: 'data_prev_inicio',
                header: 'DATA PREV. ÍNICIO',
                accessor: 'data_prev_inicio',
                sortable: true,
                width: '140px',
                align: 'center',
                render: (value) => (value ? formatarData(value) : '-'),
            },
            {
                id: 'data_inicio',
                header: 'DATA ÍNICIO',
                accessor: 'data_inicio',
                sortable: true,
                width: '120px',
                align: 'center',
                render: (value) => (value ? formatarData(value) : '-'),
            },
            {
                id: 'data_prev_fim',
                header: 'DATA PREV. FIM',
                accessor: 'data_prev_fim',
                sortable: true,
                width: '130px',
                align: 'center',
                render: (value) => (value ? formatarData(value) : '-'),
            },
            {
                id: 'data_final',
                header: 'DATA FIM',
                accessor: 'data_final',
                sortable: true,
                width: '120px',
                align: 'center',
                render: (value) => (value ? formatarData(value) : '-'),
            },
            {
                id: 'status',
                header: 'STATUS',
                accessor: 'status',
                sortable: true,
                width: '130px',
                align: 'center',
            },
            {
                id: 'iso_9001',
                header: '9001:2015',
                accessor: 'iso_9001',
                sortable: true,
                width: '100px',
                align: 'center',
                render: (value) => `${value ?? 0}/66`,
            },
            {
                id: 'iso_14001',
                header: '14001:2015',
                accessor: 'iso_14001',
                sortable: true,
                width: '100px',
                align: 'center',
                render: (value) => `${value ?? 0}/38`,
            },
            {
                id: 'iso_45001',
                header: '45001:2018',
                accessor: 'iso_45001',
                sortable: true,
                width: '100px',
                align: 'center',
                render: (value) => `${value ?? 0}/49`,
            },
            {
                id: 'percent',
                header: '%',
                accessor: 'percent',
                sortable: true,
                width: '80px',
                align: 'center',
                render: (value) => `${value ?? '100.00'}%`,
                sortValue: (row) => Number(row.percent ?? 100),
            },
            {
                id: 'acoes',
                header: 'AÇÕES',
                sortable: false,
                width: '130px',
                align: 'center',
                render: (_, row) => {
                    const temHistorico =
                        row.reprogramada === '*' || row.cancelada === '*';

                    return (
                        <div className={style.acoesCelula}>
                            <button
                                type="button"
                                className={style.botaoAcao}
                                title="Reprogramar"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    editItem(row.id);
                                }}
                            >
                                <FiEdit2 size={14} />
                            </button>
                            <button
                                type="button"
                                className={
                                    temHistorico
                                        ? style.botaoAcao
                                        : `${style.botaoAcao} ${style.botaoAcaoDisabled}`
                                }
                                title={
                                    temHistorico
                                        ? 'Ver histórico'
                                        : 'Sem histórico de reprogramação ou cancelamento'
                                }
                                disabled={!temHistorico}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (temHistorico) {
                                        openView(row.id);
                                    }
                                }}
                            >
                                <FiEye size={14} />
                            </button>
                            <button
                                type="button"
                                className={style.botaoAcao}
                                title="Planejamento"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                        `/modulo/ssma/planejamento-auditoria/${row.identifier}`
                                    );
                                }}
                            >
                                <FiCalendar size={14} />
                            </button>
                            <button
                                type="button"
                                className={`${style.botaoAcao} ${style.botaoAcaoDanger}`}
                                title="Remover da lista"
                                onClick={(e) => handleSoftDelete(e, row.identifier)}
                            >
                                <FiTrash2 size={14} />
                            </button>
                        </div>
                    );
                },
            },
        ],
        [editItem, openView]
    );

    return (
        <div className={style.container} >
            <GenericHeader responsive>
                <Filters columns={3}>
                    <MultiselectCheck
                        options={opcoesObrasMs}
                        selectedOptions={obrasSelected}
                        setSelectedOptions={setObrasSelected}
                        placeholder="CENTRO CUSTO..."
                    />
                    <MultiselectCheck
                        options={opcoesContratosMs}
                        selectedOptions={contratosSelected}
                        setSelectedOptions={setContratosSelected}
                        placeholder="CONTRATO..."
                    />
                    <MultiselectCheck
                        options={opcoesStatusMs}
                        selectedOptions={statusSelected}
                        setSelectedOptions={setStatusSelected}
                        placeholder="STATUS..."
                    />
                    <MultiselectCheck
                        options={opcoesAuditoresMs}
                        selectedOptions={auditoresSelected}
                        setSelectedOptions={setAuditoresSelected}
                        placeholder="AUDITOR..."
                    />
                    <FilterContainer
                        type="date"
                        placeholder="DATA PREV. INÍCIO — DE"
                        valor={startDate}
                        changeFunc={setStartDate}
                    />
                    <FilterContainer
                        type="date"
                        placeholder="DATA PREV. FIM — ATÉ"
                        valor={endDate}
                        changeFunc={setEndDate}
                    />
                </Filters>

                <Statistics>
                    <ContainerStatistic>
                        <TituloStatistic>ESTATÍSTICAS</TituloStatistic>
                        <TextoStatistic>PREVISTO: {previstos}</TextoStatistic>
                        <TextoStatistic>ABERTO: {abertos}</TextoStatistic>
                        <TextoStatistic>FINALIZADO: {finalizados}</TextoStatistic>
                    </ContainerStatistic>
                </Statistics>

                <Statistics>
                    <ContainerStatistic>
                        <TituloStatistic>RESUMO</TituloStatistic>
                        <TextoStatistic>CANCELADOS: {cancelados}</TextoStatistic>
                        <TextoStatistic>EM ATRASO: 0</TextoStatistic>
                        <TextoStatistic>REPROGRAMADAS: {reprogramados}</TextoStatistic>
                    </ContainerStatistic>
                </Statistics>
            </GenericHeader>

            <div className={style.tableFlexFill}>
                <SortableTable
                    ref={tabelaRef}
                    columns={columns}
                    data={dados}
                    limit={visibleCount}
                    onScrollReachEnd={loadMoreRows}
                    scrollReachThreshold={SCROLL_THRESHOLD}
                    emptyText="Nenhuma auditoria encontrada."
                    initialSort={{ key: 'id', direction: 'desc' }}
                    getRowId={(row) => row.id}
                    onRowClick={(row) =>
                        window.open(`/modulo/ssma/auditoria-interna/${row.identifier}`)
                    }
                    rowCursor="pointer"
                />
            </div>
        </div>
    )

}

export default AuditBody;
